import type { ShippingMethodQuoteFragment } from '#graphql-operations'

export function getCourierFromShippingMethodCode(code: string) {
  return code.split('_').slice(2).join('_')
}

/** Delivery-provider strategy codes used by `getDeliveryProviders`. */
export type PickupProvider = 'ppl' | 'zasilkovna'

export interface PickupInfo {
  /** Whether this method delivers to a pickup point/branch the customer must choose. */
  branches: boolean
  /** Which delivery-provider strategy backs the branch lookup, if any. */
  provider: PickupProvider | null
  /**
   * Balíkobot `service_type` of this courier in the destination country, narrowing the
   * provider's feed to the sub-carrier the customer picked. One `zasilkovna` feed holds a
   * country's whole pickup network (InPost and Poczta Polska in PL, Bartolini and the Italian
   * post in IT, …), and point ids are `<service_type>-<branch_id>`, so without this every
   * picker would offer every carrier's points. `null` where the country runs a single network
   * (CZ/SK) and the whole feed is correct.
   */
  serviceType: string | null
}

/**
 * Derive pickup-point requirements for a shipping method from its courier
 * config, matched to the order's destination country.
 *
 * - `branches === false` → ordinary delivery, nothing else to select.
 * - `branches === true` + `ppl` present → PPL locker selection (provider `ppl`).
 * - `branches === true` + `zasilkovna` present → Zásilkovna point selection.
 */
export function getPickupInfo(
  method: ShippingMethodQuoteFragment | undefined | null,
  countryCode?: string | null,
): PickupInfo {
  const configs = method?.courier?.deliveryOptions
  if (!configs?.length)
    return { branches: false, provider: null, serviceType: null }

  const cc = countryCode?.toUpperCase()
  const config = (cc ? configs.find(c => c.country?.toUpperCase() === cc) : undefined) ?? configs[0]
  if (!config?.branches)
    return { branches: false, provider: null, serviceType: null }

  const provider: PickupProvider | null = config.ppl ? 'ppl' : config.packeta ? 'zasilkovna' : null
  // `carrierId` is the courier's Balíkobot service type for this config's country — the
  // backend resolves it per (country, courier), so the same code can be a different carrier
  // in each market.
  const serviceType = config.packeta?.carrierId != null ? String(config.packeta.carrierId) : null
  return { branches: true, provider, serviceType }
}

/** Convenience guard: does this method require the customer to pick a branch? */
export function methodRequiresPickupPoint(
  method: ShippingMethodQuoteFragment | undefined | null,
  countryCode?: string | null,
): boolean {
  return getPickupInfo(method, countryCode).branches
}
